import * as React from "react";
import { Spinner, Button } from "@blueprintjs/core";
import { css } from "@emotion/core";
import { useQueryClient } from "@tanstack/react-query";
import { SurveyWithExtras } from "interfaces";
import { useTags } from "hooks/useTags";
import { TagMachineProvider } from "./TagEditorContext";
import { TagEditor } from "./TagEditor";

export interface TagEditorSlotProps {
  survey: SurveyWithExtras;
  canDeleteTags: boolean;
  resink: () => void;
  fontSize?: string;
  marginRight?: string;
}

function tagsLoadErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? "Failed to load tags");
}

export function TagEditorSlot({
  survey,
  canDeleteTags,
  resink,
  fontSize = "0.75rem",
  marginRight = "0.5rem",
}: TagEditorSlotProps) {
  const queryClient = useQueryClient();
  const { isLoading, isError, refetch, error } = useTags();

  if (isLoading) {
    return <Spinner size={20} intent="primary" />;
  }

  if (isError) {
    return (
      <>
        <span
          css={css`
            color: var(--red2);
            font-size: ${fontSize};
            margin-right: ${marginRight};
          `}
        >
          {tagsLoadErrorMessage(error)}
        </span>
        <Button
          small
          minimal
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </>
    );
  }

  return (
    <TagMachineProvider
      options={{
        input: {
          surveyId: survey.id,
          surveyTagIds: survey.tags ?? [],
          onSync: resink,
          onTagsCatalogChanged: () => {
            void queryClient.invalidateQueries({ queryKey: ["tags"] });
          },
          canDelete: canDeleteTags,
        },
      }}
    >
      <TagEditor />
    </TagMachineProvider>
  );
}
